import { Ref } from 'preact';
import { MutableRef, useMemo, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { isMobile } from '#utils/UNSAFE_clientUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { navigationListStyles } from './themes/NavigationListStyles.css';
import { getItemAttribute, REMOVE_ICON_SELECTOR, useNavigationList } from './useNavigationList';
import { forwardRef, useImperativeHandle } from 'preact/compat';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { NavigationListProps, NavigationListContext } from '../UNSAFE_NavigationListCommon';
import { TabBarContextMenu } from '../UNSAFE_TabBar/TabBarContextMenu';
import type { TabBarContextMenuConfig as NavigationListContextMenuConfig } from '../UNSAFE_TabBarCommon';
export {
  RemovableNavigationListItem,
  NavigationListLinkItem
} from '../UNSAFE_NavigationListCommon';

export type FocusableHandle = {
  focus: () => void;
  blur: () => void;
};

export const NavigationList = forwardRef(
  <K extends string | number>(
    {
      children,
      selection,
      edge = 'start',
      onSelectionChange,
      onRemove,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelLedBy,
      testId,
      display = 'standard'
    }: NavigationListProps<K> & TestIdProps,
    ref?: Ref<FocusableHandle>
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const isMobileDevice = isMobile();
    const defaultContextMenuConfig = useMemo<
      NavigationListContextMenuConfig<string | number> | undefined
    >(() => {
      return isMobileDevice
        ? {
            itemsRenderer: (context) => {
              //Only if context is available, it is a mobile device and it is removable item, we will return remove context menu item
              if (context) {
                const isRemovableItem =
                  getItemAttribute(context.itemKey, containerRef.current!, REMOVE_ICON_SELECTOR) !=
                  null;
                if (isRemovableItem) {
                  const defaultItems = context.defaultMenuItems;
                  return <>{defaultItems}</>;
                }
              }
              return null;
            }
          }
        : undefined;
    }, [isMobileDevice]);

    const {
      showFocusRing,
      currentKey,
      onCurrentKeyChange,
      containerHandlers,
      onKeyDown,
      contextMenuContext,
      menuProps,
      contextMenuDescription,
      contextMenuDescriptionId,
      accStatusInfo
    } = useNavigationList({
      containerRef: containerRef as MutableRef<HTMLDivElement>,
      selection,
      onRemove,
      onSelectionChange,
      children,
      contextMenuConfig: defaultContextMenuConfig
    });

    const eventProps = { onKeyDown };
    const navigationListClass = classNames([navigationListStyles.uListStyle]);
    const navigationListItemPrefix = useId() + '_';
    const testIdProps = useTestId(testId);

    //Allows to call focus on rootRef without having to expose it
    useImperativeHandle(ref!, () => ({
      focus: () => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      },
      blur: () => {
        if (containerRef.current) {
          containerRef.current.focus();
        }
      }
    }));

    return (
      <>
        <div
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelLedBy}
          role="tablist"
          ref={containerRef}
          aria-orientation={'vertical'}
          tabIndex={0}
          aria-activedescendant={currentKey ? navigationListItemPrefix + currentKey : ''}
          {...mergeProps(eventProps, containerHandlers)}
          {...testIdProps}
          class={navigationListClass}>
          <NavigationListContext.Provider
            value={{
              selection,
              display,
              onSelectionChange,
              onCurrentKeyChange,
              currentKey,
              showFocusRing,
              navigationListItemPrefix,
              onRemove,
              edge
            }}>
            {children}
          </NavigationListContext.Provider>
        </div>
        {defaultContextMenuConfig && contextMenuDescriptionId && contextMenuDescription && (
          <span id={contextMenuDescriptionId}>
            <HiddenAccessible>{contextMenuDescription}</HiddenAccessible>
          </span>
        )}
        {defaultContextMenuConfig && accStatusInfo && <LiveRegion>{accStatusInfo}</LiveRegion>}
        {defaultContextMenuConfig && (
          <TabBarContextMenu
            {...menuProps}
            contextMenuConfig={defaultContextMenuConfig}
            contextMenuContext={contextMenuContext!}
          />
        )}
      </>
    );
  }
);
